import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fullUrl } from "@/components/app/ImageUploader";
import StoriesViewer from "@/components/app/StoriesViewer";
import { useAuth } from "@/context/AuthContext";

/**
 * MapStoriesStrip
 * Horizontal row of Instagram-style bubbles for events that have posts.
 * Fetches posts lazily when the user opens a bubble.
 */
export default function MapStoriesStrip({ events, onChanged }) {
  const { user } = useAuth();
  const [openEventId, setOpenEventId] = useState(null);
  const [posts, setPosts] = useState([]);
  const [eventName, setEventName] = useState("");
  const [eventIsMine, setEventIsMine] = useState(false);

  const withStories = events.filter((e) => e.has_posts && e.posts_count > 0);

  const open = async (ev) => {
    try {
      const { data } = await api.get(`/events/${ev.id}/posts`);
      if (!data.length) return;
      setPosts(data);
      setEventName(ev.name);
      setEventIsMine(!!ev.is_mine);
      setOpenEventId(ev.id);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!openEventId) return;
    const stillHasIt = withStories.find((e) => e.id === openEventId);
    if (!stillHasIt) setOpenEventId(null);
  }, [events, openEventId, withStories]);

  if (!withStories.length) return null;

  return (
    <div className="absolute left-0 right-0 z-[500] px-3" style={{ top: "118px" }} data-testid="stories-strip">
      <div className="flex gap-2.5 overflow-x-auto scrollbar-none">
        {withStories.map((ev) => (
          <button
            key={ev.id}
            onClick={() => open(ev)}
            className="flex flex-col items-center gap-1 shrink-0 press"
            data-testid={`story-bubble-${ev.id}`}
          >
            <div className="relative h-16 w-16">
              <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-secondary via-primary to-secondary animate-spin-slow" />
              <div className="absolute inset-[2px] rounded-full bg-background" />
              <div className="relative m-[4px] h-[52px] w-[52px] rounded-full overflow-hidden bg-muted grid place-items-center text-xs font-bold">
                {ev.cover_url ? (
                  <img src={fullUrl(ev.cover_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl">
                    {{ churrasco: "🔥", resenha: "🍻", festa: "🎉", atletica: "🏆", luau: "🌙", show: "🎸", feira: "🛍️", outro: "📍" }[ev.type] || "📍"}
                  </span>
                )}
              </div>
              {ev.is_live && (
                <span className="absolute -bottom-0 -right-0 h-5 w-5 rounded-full bg-secondary border-2 border-background grid place-items-center">
                  <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                </span>
              )}
            </div>
            <div className="text-[10px] leading-tight font-medium max-w-[72px] truncate text-foreground/90">{ev.name}</div>
          </button>
        ))}
      </div>

      {openEventId && posts.length > 0 && (
        <StoriesViewer
          posts={posts}
          startIndex={0}
          canDelete={(p) => user && (p.user_id === user.id || eventIsMine || user.role === "admin")}
          onClose={() => { setOpenEventId(null); onChanged?.(); }}
          onDeleted={() => onChanged?.()}
        />
      )}
    </div>
  );
}
